/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import LogoRingSpinner from "@/shared/ui/components/basic/loading/LogoRingSpinner";

interface LoadingPageProps {
  isActive?: boolean;
  className?: string;
  style?: React.CSSProperties;
  /** When false, renders just the spinner without a fixed overlay wrapper. Defaults to true. */
  overlay?: boolean;
}

/**
 * Full-page loading indicator with progressive status messages,
 * Logo + Ring Spinner animation, and its own full-screen overlay backdrop.
 * Safe to use as a standalone Suspense fallback — always covers the viewport.
 */
export const LoadingPage: React.FC<LoadingPageProps> = ({ isActive = true, className, style, overlay = true }) => {
  const { t } = useTranslation("common");
  const [baseMessage, setBaseMessage] = useState("");
  const [ellipsis, setEllipsis] = useState("");
  const sessionRef = useRef(0);
  const prevActiveRef = useRef(false);

  useEffect(() => {
    const wasActive = prevActiveRef.current;
    prevActiveRef.current = isActive;

    if (!isActive) return;
    if (wasActive) return;

    sessionRef.current += 1;
    const session = sessionRef.current;

    setBaseMessage(t("loading.initial"));
    setEllipsis("");

    const ellipsisStates = ["", ".", "..", "..."];
    let ellipsisIndex = 0;
    let currentInterval: ReturnType<typeof setInterval> | null = null;
    let timer1: ReturnType<typeof setTimeout> | null = null;
    let timer2: ReturnType<typeof setTimeout> | null = null;

    const isStale = () => sessionRef.current !== session;

    currentInterval = setInterval(() => {
      if (isStale()) return;
      const next = (ellipsisIndex + 1) % ellipsisStates.length;
      ellipsisIndex = next;
      setEllipsis(ellipsisStates[ellipsisIndex]);
    }, 500);

    timer1 = setTimeout(() => {
      if (isStale()) return;
      if (currentInterval) clearInterval(currentInterval);
      ellipsisIndex = 0;
      setBaseMessage(t("loading.takingTime"));
      setEllipsis(ellipsisStates[ellipsisIndex]);

      currentInterval = setInterval(() => {
        if (isStale()) return;
        const next = (ellipsisIndex + 1) % ellipsisStates.length;
        ellipsisIndex = next;
        setEllipsis(ellipsisStates[ellipsisIndex]);
      }, 500);

      timer2 = setTimeout(() => {
        if (isStale()) return;
        if (currentInterval) clearInterval(currentInterval);
        ellipsisIndex = 0;
        setBaseMessage(t("loading.processing"));
        setEllipsis(ellipsisStates[ellipsisIndex]);

        currentInterval = setInterval(() => {
          if (isStale()) return;
          const next = (ellipsisIndex + 1) % ellipsisStates.length;
          ellipsisIndex = next;
          setEllipsis(ellipsisStates[ellipsisIndex]);
        }, 500);
      }, 9000);
    }, 5000);

    return () => {
      if (currentInterval) clearInterval(currentInterval);
      if (timer1) clearTimeout(timer1);
      if (timer2) clearTimeout(timer2);
    };
  }, [isActive, t]);

  const spinner = <LogoRingSpinner text={`${baseMessage}${ellipsis}`} />;

  if (!overlay) {
    return (
      <div data-testid="loading-page" className={className} style={style}>
        {spinner}
      </div>
    );
  }

  return (
    <div
      data-testid="loading-page"
      className={className}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100vw",
        height: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "var(--surface-primary, #faf8f5)",
        zIndex: 9999,
        ...style,
      }}
    >
      {spinner}
    </div>
  );
};
